// Fichier: src/js/config/effects-config.js
// Configuration d'intégration des effets visuels mise à jour avec les effets de scroll

export const effectsConfig = {
  // Configuration globale des effets
  global: {
    enableOnMobile: false, // Désactiver les effets lourds sur mobile
    enableCursor: true,    // Curseur personnalisé
    enableParticles: true, // Particules de fond
    enableTransitions: true, // Transitions de page
    performanceMode: 'auto' // 'auto', 'high', 'low'
  },

  // ===== NOUVEAUX EFFETS DE SCROLL =====
  scrollEffects: {
    // Configuration parallax
    parallax: {
      enabled: true,
      layers: [0.2, 0.4, 0.6, 0.8], // Vitesses des différentes couches
      throttle: 16, // ms - fréquence de mise à jour
      disableOnMobile: true,
      debug: false // Afficher les bordures des couches en mode dev
    },

    // Configuration des révélations au scroll
    reveal: {
      enabled: true,
      offset: 80, // px - distance avant déclenchement
      threshold: 0.08, // Pourcentage de l'élément visible
      stagger: 35, // ms - délai entre éléments
      easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      duration: 1200 // ms
    },

    // Configuration des compteurs animés
    counters: {
      enabled: true,
      duration: 1500, // ms - durée de l'animation
      easing: 'easeOutQuart',
      formatNumbers: true, // Ajouter des séparateurs de milliers
      triggerOnce: true // Ne déclencher qu'une fois
    },

    // Configuration de la barre de progression
    progress: {
      enabled: true,
      selector: '.scroll-progress',
      height: 4, // px
      smooth: true,
      showPercentage: false
    },

    // Configuration typewriter
    typewriter: {
      enabled: true,
      speed: 50, // ms entre chaque caractère
      delay: 1000, // ms avant de commencer
      cursor: true,
      cursorChar: '|',
      loop: false
    },

    // Formes flottantes
    floatingShapes: {
      enabled: true,
      count: 5,
      duration: 20000, // ms pour traverser l'écran
      shapes: ['circle', 'triangle', 'square', 'diamond'],
      sizes: [40, 60, 80], // px
      disableOnMobile: true
    }
  },

  // Effets par section (conservés de votre config existante + nouveaux)
  sections: {
    hero: {
      effects: ['aurora', 'morphing-shapes', 'particles', 'typewriter', 'parallax', 'floating-shapes'],
      particleCount: 100,
      auroraIntensity: 0.3,
      parallaxLayers: 3,
      typewriterText: "Développeur Full-Stack spécialisé en IA"
    },
    about: {
      effects: ['mesh-gradient', 'holographic-cards', 'breathing-glow', 'scroll-reveal'],
      cardAnimation: 'stack',
      revealDirection: 'up'
    },
    research: {
      effects: ['dna-helix', 'vortex', 'crystal-cards', 'counters'],
      helixSpeed: 4,
      vortexLayers: 3,
      counterTargets: {
        publications: 15,
        citations: 120,
        collaborations: 8
      }
    },
    projects: {
      effects: ['3d-flip', 'spotlight', 'tilt', 'cyber-grid', 'stagger-reveal'],
      flipDelay: 300,
      tiltMaxAngle: 15,
      staggerDelay: 150
    },
    skills: {
      effects: ['sound-waves', 'skill-bars-gradient', 'framework-hover', 'progress-bars'],
      soundBars: 7,
      animationDelay: 100,
      skillLevels: {
        javascript: 95,
        python: 90,
        react: 88,
        nodejs: 85
      }
    },
    experience: {
      effects: ['timeline-flow', 'timeline-pulse', 'hover-glow', 'reveal-cascade'],
      pulseInterval: 2000,
      cascadeDelay: 200
    },
    contact: {
      effects: ['fireflies', 'cosmic-dust', 'energy-shield', 'liquid-buttons', 'form-validation'],
      fireflyCount: 15,
      dustParticles: 50,
      validationEffects: true
    }
  },

  // Configuration des animations de texte (conservée + améliorée)
  textAnimations: {
    wave: {
      amplitude: 20,
      frequency: 2,
      delay: 0.05
    },
    glitch: {
      duration: 500,
      intensity: 2
    },
    scramble: {
      duration: 500,
      frameRate: 30
    },
    typewriter: {
      speed: 100,
      cursor: true,
      cursorBlink: true
    },
    rotating: {
      interval: 3000,
      transition: 'ease-in-out'
    },
    // NOUVEAUX
    fadeInUp: {
      distance: 50,
      duration: 800,
      easing: 'cubic-bezier(0.16, 1, 0.3, 1)'
    },
    slideInLeft: {
      distance: 100,
      duration: 600,
      easing: 'ease-out'
    },
    scaleIn: {
      scale: 0.8,
      duration: 500,
      easing: 'back.out(1.7)'
    }
  },

  // Configuration des boutons (conservée)
  buttons: {
    primary: {
      effects: ['liquid', 'ripple', 'magnetic'],
      magneticForce: 0.3,
      rippleDuration: 600
    },
    secondary: {
      effects: ['neon', 'glow'],
      glowIntensity: 0.5
    },
    outline: {
      effects: ['elastic', '3d-press'],
      elasticForce: 1.55
    }
  },

  // Configuration des cartes (conservée + améliorée)
  cards: {
    project: {
      effects: ['3d-flip', 'holographic', 'spotlight', 'tilt', 'scroll-reveal'],
      flipDuration: 800,
      holographicOpacity: 0.3,
      revealAnimation: 'slideInUp'
    },
    stat: {
      effects: ['stack', 'breathing-glow', 'magnetic', 'counter'],
      stackOffset: 10,
      glowCycle: 4000,
      counterDuration: 2000
    },
    publication: {
      effects: ['crystal', 'hover-lift', 'fade-in'],
      crystalSpeed: 600,
      liftDistance: 10
    }
  },

  // Configuration du curseur (conservée)
  cursor: {
    size: 20,
    dotSize: 4,
    lerpFactor: 0.1,
    dotLerpFactor: 0.5,
    hoverScale: 2,
    borderWidth: 2
  },

  // Configuration des particules (conservée)
  particles: {
    quantum: {
      count: 30,
      size: { min: 1, max: 3 },
      speed: { min: 10, max: 20 },
      opacity: { min: 0.2, max: 0.8 }
    },
    firefly: {
      count: 15,
      size: 4,
      glowRadius: 10,
      lifetime: 20000
    },
    dust: {
      count: 50,
      size: 1,
      speed: { min: 10, max: 30 }
    },
    matrix: {
      columns: 'auto',
      fontSize: 20,
      speed: 10,
      color: '#00FF00'
    }
  },

  // Breakpoints pour la responsive (conservés)
  breakpoints: {
    mobile: 576,
    tablet: 768,
    desktop: 992,
    large: 1200
  },

  // Optimisation des performances (améliorée)
  performance: {
    throttleScroll: 16,    // ms
    debounceResize: 250,   // ms
    lazyLoadOffset: 100,   // px
    maxParticles: 100,
    disableOnBattery: true, // Désactiver si batterie faible
    reducedMotion: true,   // Respecter prefers-reduced-motion
    fpsCap: 60,           // Limiter les FPS
    useRAF: true,         // Utiliser requestAnimationFrame
    observerOptions: {
      rootMargin: '0px 0px -50px 0px',
      threshold: [0, 0.25, 0.5, 0.75, 1]
    }
  },

  // Transitions (améliorées)
  transitions: {
    page: {
      duration: 1500,
      easing: 'cubic-bezier(0.4, 0, 0.2, 1)'
    },
    section: {
      duration: 800,
      stagger: 100,
      easing: 'ease-out'
    },
    element: {
      duration: 300,
      easing: 'ease'
    },
    // NOUVEAUX
    reveal: {
      duration: 800,
      easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
      stagger: 100
    },
    parallax: {
      lerp: 0.1, // Facteur de lissage
      precision: 0.1 // Précision des calculs
    }
  },

  // ===== NOUVELLES CONFIGURATIONS =====

  // Configuration pour le mode debug
  debug: {
    enabled: false, // Activer en mode développement
    showBounds: false, // Afficher les bordures des éléments
    logPerformance: false, // Logger les métriques de performance
    visualizeScroll: false, // Visualiser les zones de déclenchement
    fpsMonitor: false // Afficher le compteur FPS
  },

  // Configuration des seuils de performance
  performanceThresholds: {
    fps: {
      good: 58,
      warning: 45,
      critical: 30
    },
    memory: {
      warning: 50, // MB
      critical: 100 // MB
    }
  },

  // Configuration des événements personnalisés
  customEvents: {
    scrollProgress: 'scroll:progress',
    sectionEnter: 'section:enter',
    sectionLeave: 'section:leave',
    counterComplete: 'counter:complete',
    revealComplete: 'reveal:complete'
  },

  // Préréglages pour différents appareils
  devicePresets: {
    mobile: {
      parallax: { enabled: false },
      particles: { count: 20 },
      animations: { reduced: true }
    },
    tablet: {
      parallax: { layers: [0.3, 0.6] },
      particles: { count: 50 }
    },
    desktop: {
      // Configuration complète par défaut
    },
    lowEnd: {
      parallax: { enabled: false },
      particles: { enabled: false },
      complexAnimations: { enabled: false }
    }
  }
};

// Fonction utilitaire pour obtenir la config selon l'appareil
export function getDeviceConfig() {
  const width = window.innerWidth;
  const memory = navigator.deviceMemory || 4; // GB
  const connection = navigator.connection?.effectiveType || '4g';
  
  if (width < effectsConfig.breakpoints.mobile || memory < 2 || connection === 'slow-2g') {
    return { ...effectsConfig, ...effectsConfig.devicePresets.lowEnd };
  }
  
  if (width < effectsConfig.breakpoints.tablet) {
    return { ...effectsConfig, ...effectsConfig.devicePresets.mobile };
  }
  
  if (width < effectsConfig.breakpoints.desktop) {
    return { ...effectsConfig, ...effectsConfig.devicePresets.tablet };
  }
  
  return effectsConfig;
}

// Fonction pour détecter les préférences utilisateur
export function getUserPreferences() {
  return {
    reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    darkMode: window.matchMedia('(prefers-color-scheme: dark)').matches,
    highContrast: window.matchMedia('(prefers-contrast: high)').matches,
    transparency: !window.matchMedia('(prefers-reduced-transparency: reduce)').matches
  };
}

export default effectsConfig;